/**
 * Speech for the hosted chat.
 *
 * Hearing runs on the server: a clip goes to `/voice`, where Hamsa and
 * gpt-transcribe listen in parallel and the transcript decides the language
 * (packages/voice). So the shopper never picks a language, and Lebanese is
 * heard by the engine built for it. Live voice is a separate call service
 * (apps/voice) driven by `call/call-engine.ts`. Replies are not read aloud
 * outside a call: the browser voices were not good enough to ship.
 */

import type { SpokenLanguage } from "@repo/voice/protocol";
import { toWav as wavFromPcm } from "@repo/voice/wav";

/* ------------------------------------------------------------------ */
/* Capabilities                                                        */
/* ------------------------------------------------------------------ */

/** iOS Safari before 18.4 can only do AAC in MP4, so the fallback chain matters. */
const MIME_PREFERENCE = [
	"audio/webm;codecs=opus",
	"audio/mp4",
	"audio/ogg;codecs=opus",
	"audio/webm",
];

export function recordingMimeType(): string | null {
	if (typeof MediaRecorder === "undefined") {
		return null;
	}
	return (
		MIME_PREFERENCE.find((type) => MediaRecorder.isTypeSupported(type)) ??
		null
	);
}

/** getUserMedia is absent on http origins other than localhost. */
export function canRecord(): boolean {
	return (
		typeof navigator !== "undefined" &&
		Boolean(navigator.mediaDevices?.getUserMedia) &&
		recordingMimeType() !== null
	);
}

/** A live call needs the mic and an AudioWorklet for its voice detection. */
export function canCall(): boolean {
	return (
		typeof window !== "undefined" &&
		Boolean(navigator.mediaDevices?.getUserMedia) &&
		"AudioWorkletNode" in window
	);
}

/* ------------------------------------------------------------------ */
/* Speech to text (server)                                             */
/* ------------------------------------------------------------------ */

const TARGET_RATE = 16_000;

/**
 * The recorded clip as 16 kHz mono WAV. Converting in the browser means the
 * server never needs ffmpeg, and Hamsa rejects anything but WAV; 16 kHz mono
 * keeps a 60 s clip under the 2 MB upload cap.
 */
export async function toWav(recorded: Blob): Promise<Blob> {
	const AudioContextCtor =
		window.AudioContext ??
		(window as unknown as { webkitAudioContext?: typeof AudioContext })
			.webkitAudioContext;
	if (!AudioContextCtor) {
		throw new Error("unsupported");
	}
	const context = new AudioContextCtor();
	try {
		const decoded = await context.decodeAudioData(
			await recorded.arrayBuffer(),
		);
		const frames = Math.ceil(decoded.duration * TARGET_RATE);
		const offline = new OfflineAudioContext(1, frames, TARGET_RATE);
		const source = offline.createBufferSource();
		source.buffer = decoded;
		source.connect(offline.destination);
		source.start();
		const rendered = await offline.startRendering();
		const samples = rendered.getChannelData(0);
		const pcm = new Int16Array(samples.length);
		for (let i = 0; i < samples.length; i++) {
			pcm[i] = Math.max(-1, Math.min(1, samples[i] ?? 0)) * 0x7fff;
		}
		return new Blob([wavFromPcm(new Uint8Array(pcm.buffer), TARGET_RATE)], {
			type: "audio/wav",
		});
	} finally {
		void context.close();
	}
}

export type Transcription =
	| { ok: true; text: string; language: SpokenLanguage }
	| {
			ok: false;
			error: "no_speech" | "rate_limited" | "paused" | "failed";
	  };

export type Transcribe = (recorded: Blob) => Promise<Transcription>;

/**
 * Transcribe only: the words come back for the shopper to see (and fix)
 * before anything is sent, so every later path is the same as a typed turn.
 */
export async function transcribeClip(
	/** The page's session-aware fetch for its public API. */
	request: (path: string, init: RequestInit) => Promise<Response>,
	/** The conversation, and for a session opened by this clip its store and embed. */
	fields: Record<string, string | null | undefined>,
	recorded: Blob,
): Promise<Transcription> {
	try {
		const form = new FormData();
		form.append("audio", await toWav(recorded), "clip.wav");
		form.append("transcribeOnly", "1");
		for (const [name, value] of Object.entries(fields)) {
			if (value) {
				form.append(name, value);
			}
		}
		const response = await request("/voice", {
			method: "POST",
			body: form,
		});
		if (!response.ok) {
			const body = (await response.json().catch(() => null)) as {
				code?: string;
				error?: string;
			} | null;
			return {
				ok: false,
				error:
					body?.code === "no_speech" || body?.error === "clip_length"
						? "no_speech"
						: body?.code === "rate_limited"
							? "rate_limited"
							: body?.code === "spend_cap_reached"
								? "paused"
								: "failed",
			};
		}
		const { text, language } = (await response.json()) as {
			text: string;
			language: SpokenLanguage;
		};
		return text.trim()
			? { ok: true, text: text.trim(), language }
			: { ok: false, error: "no_speech" };
	} catch {
		return { ok: false, error: "failed" };
	}
}
